import { useContext, useState } from "react";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import {
  createDocumentGat,
  createDocumentSignTypeGat,
  createSignUserGat,
  deleteDocumentSignTypeGat,
  downloadDocumentGat,
  getDocumentsGat,
  getDocumentSignGat,
  getSignerLinksGat,
  getSignersEditGat,
  getSignersGat,
  resendEmailGat,
  resendEmailWithTokensGat,
  ResendSignerItem,
  SignerLinkItem,
  updateDocumentSignTypeGat,
} from "../../gateways/DocumentGateway";
import { AuthContext } from "../AuthContext";
import { UserTypeContext } from "../UserTypeContext";
import { getUserLocationString } from "../../utils/getUserLocationString";
import formatDateBr from "../../utils/formatDateBr";
import { jwtDecode } from "jwt-decode";
import { UserToken } from "./useAuth";
import { SettingsContext } from "../SettingsContext";

export interface DocumentSignType {
  documentOwnerName: string;
  documentOwnerEmail: string;
  documentId: string;
  signerActionsRequest: SignerActionRequest[];
  messageTitle: string;
  messageEmailBody?: string;
}

export interface SignerActionRequest {
  signerEmail: string;
  signType: number;
  hasSigned: boolean;
  presencialSign: boolean;
  photoRequired: boolean;
  signatureLocations: SignatureLocation[];
  signerName: string;
  userDocumentOwnerId: string;
}

export interface SignatureLocation {
  positonX: number;
  postionY: number;
  pageNumber: number;
  width: number;
  height: number;
}

export interface UserSigner {
  signActionId?: string;
  documentId?: string;
  signerUserId?: string;
  signature?: any;
  name?: string;
  nationalRegisterCode?: string;
  signerIp?: string;
  location?: string;
  photo?: any;
}

export default function useDocument() {
  const showToast = useToast();
  const { setLoading } = useContext(LoadingContext);
  const { handleLogout } = useContext(AuthContext);
  const {getMessageTitleByUserId} = useContext(SettingsContext);
  const { ipUser, formValuesUsers } = useContext(UserTypeContext);
  const [document, setDocument] = useState<any>("");
  const [signers, setSigners] = useState<any>([]);
  const [signersEdit, setSignersEdit] = useState<any>([]);
  const [infoSign, setInfoSign] = useState<any>(null);
  const [nameDocument, setNameDocument] = useState("");
  const [idEditDocument, setIdEditDocument] = useState("");
  const [tableDocuments, setTableDocuments] = useState<any[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [collaborators, setCollaborators] = useState<any>([]);
  const [tutorial, setTutorial] = useState(false);

  const [formValuesSign, setFormValuesSign] = useState<UserSigner>({
    signActionId: "",
    documentId: "",
    signerUserId: "",
    signature: "",
    name: "",
    nationalRegisterCode: "",
    signerIp: "",
    location: "",
    photo: "",
  });

  const [formValuesDocumentSign, setFormValuesDocumentSign] =
    useState<DocumentSignType>({
      documentOwnerName: "",
      documentOwnerEmail: "",
      documentId: "",
      signerActionsRequest: [
        {
          signerEmail: "",
          signerName: "",
          userDocumentOwnerId: "",
          signType: 0,
          hasSigned: false,
          presencialSign: false,
          photoRequired: false,
          signatureLocations: [
            {
              positonX: 0,
              postionY: 0,
              pageNumber: 0,
              width: 0,
              height: 0,
            },
          ],
        },
      ],
      messageTitle: "",
      messageEmailBody: "",
    });

  let objUserTk: UserToken;
  if (localStorage.getItem("token")) {
    objUserTk = jwtDecode(localStorage.getItem("token") || "");
  } else {
    objUserTk = {} as UserToken;
  }

  const createDocument = async (documentFile: any) => {
    try {
      setLoading(true);

      if (nameDocument.trim() === "") {
        showToast("info", "Nome do Documento Obrigatório");
        setLoading(false);
        return "nonename";
      }

      const result = await createDocumentGat(
        documentFile,
        objUserTk.UserId,
        nameDocument
      );
      if (result) {
        // showToast(
        //   "success",
        //   "Sucesso ao cadastrar"
        // );
        setLoading(false);
        return result[0]?.data;
      }
    } catch (error: any) {
      console.log(error);
      showToast(
        "error",
        error?.response?.data?.error?.message || "Erro ao cadastrar"
      );
      setLoading(false);
    }
  };

  const byteArrayToBase64 = (uint8Array: Uint8Array | number[]) => {
    let binary = "";
    uint8Array.forEach((byte: number) => (binary += String.fromCharCode(byte)));
    return window.btoa(binary);
  };

  const createSignUser = async (dataURL: string, photoSignerDocument?: number[]) => {
    try {
      const byteString = atob(dataURL.split(",")[1]);
      const byteArray = new Uint8Array(byteString.length);
  
      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }
  
      const base64Signature = byteArrayToBase64(byteArray);
  
      setFormValuesSign({
        ...formValuesSign,
        signature: base64Signature,
      });
  
      let local;
      try {
        local = await getUserLocationString();
      } catch (error) {
        console.error(`Erro ao obter localização: ${error}`);
        local = "Localidade não informada";
      }
  
      let photoSignerBase64 = null;
      if (photoSignerDocument) {
        photoSignerBase64 = byteArrayToBase64(photoSignerDocument);
      }
  
      const obj = {
        ...formValuesSign,
        documentId: infoSign.documentId ?? undefined,
        signActionId: infoSign.signerActionId ?? undefined,
        signerUserId: infoSign.userId ?? undefined,
        signature: base64Signature,
        signerIp: ipUser,
        location: local,
        name: formValuesUsers.name,
        photoSignerDocument: photoSignerBase64,
        nationalRegisterCode: formValuesSign.nationalRegisterCode?.replace(/\D/g, ''),
      };
  
      const result = await createSignUserGat(obj);
      if (result) {
        showToast("success", "Documento enviado para assinatura!");
        await getSigners(obj.documentId);
      }
    } catch (error: any) {
      console.log(error);
      showToast(
        "error",
        error?.response?.data?.error?.message || "Erro ao cadastrar"
      );
      throw error;
    }
  };
  

  const createDocumentSignType = async (document: DocumentSignType) => {
    try {
      setLoading(true);
      const result = await createDocumentSignTypeGat(document);
      if (result) {
        showToast("success", "Sucesso ao cadastrar");
        getDocuments(1);
        setLoading(false);
      }
    } catch (error: any) {
      console.log(error);
      showToast(
        "error",
        error?.response?.data?.error?.message || "Erro ao cadastrar"
      );
      setLoading(false);
    }
  };

  const updateDocumentSignType = async (document: DocumentSignType) => {
    try {
      setLoading(true);
      const result = await updateDocumentSignTypeGat(document);
      if (result) {
        showToast("success", "Sucesso ao atualizar");
        getDocuments(1);
        setLoading(false);
      }
    } catch (error: any) {
      console.log(error);
      showToast(
        "error",
        error?.response?.data?.error?.message || "Erro ao cadastrar"
      );
      setLoading(false);
    }
  };

  const deleteDocumentSignType = async (documentId: string) => {
    try {
      setLoading(true);
      const result = await deleteDocumentSignTypeGat(documentId);
      if (result) {
        showToast("success", "Sucesso ao deletar");
        getDocuments(1);
        setLoading(false);
      }
    } catch (error: any) {
      console.log(error);
      showToast(
        "error",
        error?.response?.data?.error?.message || "Erro ao cadastrar"
      );
      setLoading(false);
    }
  };

  const getDocumentSign = async (documentId: string, suppressErrorToast = false) => {
    try {
      setLoading(true);
      const result = await getDocumentSignGat(documentId);
      if (result) {
        setDocument(result.data.data);
        return result.data.data;
      }
    } catch (error: any) {
      console.log(error);
      if (!suppressErrorToast) {
        showToast("error", error?.message || "Erro ao carregar signatários");
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const getSigners = async (documentId: string, suppressErrorToast = false) => {
    try {
      setLoading(true);
      const result = await getSignersGat(documentId);
      if (result) {
        setSigners(result.data.data);
        return result.data.data;
      }
    } catch (error: any) {
      console.log(error);
      if (!suppressErrorToast) {
        showToast("error", error?.message || "Erro ao carregar signatários");
      }
    } finally {
      setLoading(false);
    }
  };

  function transformToImageFormat(data: any) {
    const result: any[] = [];
    let idCounter = 1;

    data?.data?.userSigners?.forEach((signer: any) => {
      const { email, name, signerActions } = signer;

      signerActions?.forEach((action: any) => {
        action.documentSignatureLocations?.forEach((location: any) => {
          result.push({
            id: idCounter++,
            type: "text",
            text: name,
            email,
            pageNum: location.pageNumber - 1,
            position: {
              x: location.positonX,
              y: location.postionY,
            },
          });
        });
      });
    });

    return result;
  }

  const getSignersEdit = async (documentId: string) => {
    try {
      setLoading(true);
      const result = await getSignersEditGat(documentId);
      if (result) {
        const transformedData = transformToImageFormat(result.data);
        setNameDocument(result.data.data.documentName);
        setSignersEdit(transformedData);
        setLoading(false);
        return transformedData;
      }
    } catch (error: any) {
      console.log(error);
      showToast("error", error?.message || "Erro ao cadastrar");
      setLoading(false);
    }
  };

  const statusMap: { [key: number]: string } = {
    0: "None",
    1: "Novo",
    2: "Aguardando Signatários",
    3: "Aguardando Assinaturas",
    4: "Assinado",
    5: "Finalizado",
    6: "Em Processamento",
    9: "Cancelado",
  };

  const getDocuments = async (
    page: number,
    documentName?: string,
    Loading = true
  ) => {
    try {
      if (Loading) setLoading(true);
      const result = await getDocumentsGat(
        objUserTk.UserId,
        page,
        5,
        documentName
      );
      if (result) {
        setTotalDocuments(Math.ceil(result.data?.data?.totalDocuments / 5));
        
        const tableResult = result.data?.data?.documents?.map((item: any) => ({
          documentOwner: item.documentOwner,
          name: item.documentName,
          data: formatDateBr(item.createdDate),
          id: item.documentId,
          status: statusMap[item.status] || "Desconhecido",
          emailsSig: item.signerEmail,
          isDocumentOwner: item.isDocumentOwner,
          signers: item.signers || [],
        }));
        
        setTableDocuments(tableResult);
        setLoading(false);
  
        if (result.data?.data?.documents?.length === 0) {
          setTutorial(true);
        }
  
        return result.data.data;
      }
    } catch (error: any) {
      console.log(error);
      if (error.status === 401) {
        showToast("error", "Sessão expirada, faça login novamente");
        handleLogout();
        window.location.href = "/auth/login";
      } else {
        showToast("error", error?.message || "Erro ao cadastrar");
        setLoading(false);
      }
    }
  };

  const downloadDocument = async (documentId: string) => {
    try {
      setLoading(true);
      await downloadDocumentGat(documentId, true);
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      showToast("error", error?.message || "Erro ao baixar");
      setLoading(false);
    }
  };

  const getSignerLinks = async (documentId: string): Promise<SignerLinkItem[]> => {
    try {
      setLoading(true);
      const result = await getSignerLinksGat(documentId);
      return result;
    } catch (error: any) {
      console.log(error);
      showToast("error", error?.response?.data?.error?.message || "Erro ao gerar links");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async (documentId: string, emails: any) => {
    try {
      setLoading(true);
      const {messageTitle, emailBody} = await getMessageTitleByUserId(objUserTk.UserId);

      const data = {
        documentEmailFromAddress: objUserTk.email,
        documentNameFromAddress: objUserTk.UserName,
        documentId: documentId,
        signerEmails: emails,
        emailMessageTitle: messageTitle,
        messageEmailBody: emailBody,
      };
      await resendEmailGat(data);
      showToast("success", "E-mail enviado com sucesso");
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      showToast("error", error?.response?.data?.error?.message || "Erro ao enviar e-mail");
      setLoading(false);
    }
  };

  const resendEmailWithTokens = async (
    documentId: string,
    signers: ResendSignerItem[]
  ) => {
    try {
      const {messageTitle, emailBody} = await getMessageTitleByUserId(objUserTk.UserId);

      const data = {
        documentEmailFromAddress: objUserTk.email || "",
        documentNameFromAddress: objUserTk.UserName,
        documentId,
        signers,
        emailMessageTitle: messageTitle,
        messageEmailBody: emailBody,
      };
      await resendEmailWithTokensGat(data);
      showToast("success", "Mensagem reenviada com sucesso");
    } catch (error: any) {
      console.log(error);
      showToast("error", error?.response?.data?.error?.message || "Erro ao reenviar mensagem");
    }
  };

  return {
    formValuesDocumentSign,
    setFormValuesDocumentSign,
    createDocument,
    createDocumentSignType,
    getDocumentSign,
    document,
    setDocument,
    formValuesSign,
    setFormValuesSign,
    createSignUser,
    infoSign,
    setInfoSign,
    getSigners,
    signers,
    getDocuments,
    nameDocument,
    setNameDocument,
    tableDocuments,
    getSignersEdit,
    signersEdit,
    updateDocumentSignType,
    idEditDocument,
    deleteDocumentSignType,
    setIdEditDocument,
    downloadDocument,
    totalDocuments,
    resendEmail,
    collaborators, 
    setCollaborators,
    setSignersEdit,
    tutorial, 
    setTutorial,
    getSignerLinks,
    resendEmailWithTokens
  };
}
